import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const NotificationModalContext = createContext();

export function useNotificationModal() {
  const context = useContext(NotificationModalContext);
  if (!context) {
    throw new Error('useNotificationModal must be used within a NotificationModalProvider');
  }
  return context;
}

export function NotificationModalProvider({ children }) {
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'alert', // 'alert', 'confirm', or 'toast'
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    onConfirm: null,
    onCancel: null,
    variant: 'default', // 'default', 'error', 'warning', 'success'
  });

  // Toast auto-dismiss timer
  const toastTimerRef = useRef(null);
  const [isToastPaused, setIsToastPaused] = useState(false);

  // Clear timer on unmount or when toast closes
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // Handle auto-dismiss for toasts
  useEffect(() => {
    if (modalState.type === 'toast' && modalState.isOpen && !isToastPaused) {
      // Only auto-dismiss success and info toasts (not error/warning)
      if (modalState.variant === 'success' || modalState.variant === 'default') {
        const duration = modalState.variant === 'success' ? 3000 : 5000;
        toastTimerRef.current = setTimeout(() => {
          setModalState(prev => ({ ...prev, isOpen: false }));
        }, duration);
      }
    }

    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, [modalState.isOpen, modalState.type, modalState.variant, isToastPaused]);

  const pauseToast = useCallback(() => setIsToastPaused(true), []);
  const resumeToast = useCallback(() => setIsToastPaused(false), []);

  const showAlert = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        type: 'alert',
        title: options.title || '',
        message,
        confirmText: options.confirmText || 'OK',
        cancelText: 'Cancel',
        variant: options.variant || 'default',
        onConfirm: () => {
          setModalState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: null,
      });
    });
  }, []);

  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        type: 'confirm',
        title: options.title || '',
        message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        variant: options.variant || 'default',
        onConfirm: () => {
          setModalState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setModalState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        },
      });
    });
  }, []);

  const showToast = useCallback((message, options = {}) => {
    // Clear any existing timer
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setIsToastPaused(false);

    setModalState({
      isOpen: true,
      type: 'toast',
      title: '',
      message,
      confirmText: '',
      cancelText: '',
      variant: options.variant || 'success',
      onConfirm: null,
      onCancel: null,
    });
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const closeModal = useCallback(() => {
    if (modalState.type === 'toast') {
      dismissToast();
      return;
    }
    if (modalState.onCancel) {
      modalState.onCancel();
    } else if (modalState.onConfirm) {
      modalState.onConfirm();
    }
  }, [modalState, dismissToast]);

  const value = {
    modalState,
    showAlert,
    showConfirm,
    showToast,
    closeModal,
    dismissToast,
    pauseToast,
    resumeToast,
  };

  return (
    <NotificationModalContext.Provider value={value}>
      {children}
    </NotificationModalContext.Provider>
  );
}
